import { datadogRum } from '@datadog/browser-rum';
import { isString } from 'lodash';
import { redactSensitiveData, redactSensitiveDataDeep } from '@nova/core';

/**
 * Check if RUM should be enabled based on environment
 * @param {import('../config/config').HeliosEnvVars} config - Application configuration object
 * @returns {boolean} - True if RUM should be enabled
 */
function shouldEnableRum(config) {
  const clientToken = config.datadog_rum_client_token?.trim();
  const applicationId = config.datadog_rum_application_id?.trim();

  // Must have credentials configured
  if (!clientToken || !applicationId) {
    return false;
  }

  // Only enable on production environment
  const env = config.server_env?.toLowerCase();
  return env === 'production' || env === 'prod';
}

/**
 * Scrub one string leaf in place. No-op when the property is missing or not a string.
 * @param {Record<string, unknown>|null|undefined} record
 * @param {string} key
 */
function scrubStringProperty(record, key) {
  if (!record || !isString(record[key])) {
    return;
  }
  record[key] = redactSensitiveData(record[key]);
}

/**
 * Replace a nested object/array field with a fail-closed deep-redacted copy.
 * @param {Record<string, unknown>} parent
 * @param {string} key
 * @returns {boolean} false when redaction failed (caller should drop the event)
 */
function scrubDeepProperty(parent, key) {
  if (parent[key] === undefined || parent[key] === null) {
    return true;
  }
  const redacted = redactSensitiveDataDeep(parent[key]);
  if (redacted === null) {
    return false;
  }
  parent[key] = redacted;
  return true;
}

/** @param {import('@datadog/browser-rum').RumEvent['view']} view */
function scrubViewFields(view) {
  if (!view) {
    return;
  }
  scrubStringProperty(view, 'url');
  scrubStringProperty(view, 'referrer');
  scrubStringProperty(view, 'name');
  const largestContentfulPaint = view.performance?.lcp;
  if (largestContentfulPaint) {
    scrubStringProperty(largestContentfulPaint, 'resource_url');
  }
}

/** @param {import('@datadog/browser-rum').RumEvent['error']} error */
function scrubErrorFields(error) {
  if (!error) {
    return;
  }
  scrubStringProperty(error, 'message');
  scrubStringProperty(error, 'stack');
  scrubStringProperty(error, 'handling_stack');
  scrubStringProperty(error, 'fingerprint');
  if (error.resource) {
    scrubStringProperty(error.resource, 'url');
  }
}

/**
 * @param {import('@datadog/browser-rum').RumEvent['resource']} resource
 * @returns {boolean}
 */
function scrubResourceFields(resource) {
  if (!resource) {
    return true;
  }
  scrubStringProperty(resource, 'url');
  if (resource.graphql) {
    scrubStringProperty(resource.graphql, 'variables');
  }
  if (resource.request && !scrubDeepProperty(resource.request, 'headers')) {
    return false;
  }
  if (resource.response && !scrubDeepProperty(resource.response, 'headers')) {
    return false;
  }
  if (resource.websocket) {
    scrubStringProperty(resource.websocket, 'close_reason');
    scrubStringProperty(resource.websocket, 'protocol');
  }
  return true;
}

/** @param {import('@datadog/browser-rum').RumEvent['long_task']} longTask */
function scrubLongTaskScripts(longTask) {
  const scripts = longTask?.scripts;
  if (!Array.isArray(scripts)) {
    return;
  }
  for (const script of scripts) {
    if (script && typeof script === 'object') {
      scrubStringProperty(script, 'source_url');
      scrubStringProperty(script, 'invoker');
    }
  }
}

/**
 * Mutate only fields Datadog RUM will copy back after beforeSend.
 *
 * RUM hands beforeSend a deep clone and persists only paths in
 * `MODIFIABLE_FIELD_PATHS_BY_EVENT` (`@datadog/browser-rum-core` assembly).
 * Walking the whole event wastes main-thread work; mutations outside that list
 * are discarded by `limitModification`.
 *
 * @param {import('@datadog/browser-rum').RumEvent} event
 * @returns {boolean} false to drop the event (deep scrub failure)
 */
export function scrubModifiableRumFields(event) {
  if (!event || typeof event !== 'object') {
    return true;
  }

  scrubViewFields(event.view);
  scrubErrorFields(event.error);
  if (!scrubResourceFields(event.resource)) {
    return false;
  }
  if (event.action?.target) {
    scrubStringProperty(event.action.target, 'name');
  }
  scrubLongTaskScripts(event.long_task);

  // context is the only common object bag; deep-scrub fail-closed.
  // service / version are modifiable strings but not JWT carriers — skip.
  return scrubDeepProperty(event, 'context');
}

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * @param {import('../config/config').HeliosEnvVars} config - Application configuration object
 */
export default function initDatadogRum(config) {
  if (!shouldEnableRum(config)) {
    console.warn('Datadog RUM is not enabled on this environment');
    return;
  }

  try {
    /**
     * Cost / volume knobs are set explicitly so v7 SDK defaults cannot silently
     * inflate RUM or APM usage. Notable SDK defaults we override or freeze:
     * sessionSampleRate 100, trackResources/trackLongTasks true, sessionReplay
     * and profiling 0, propagateTraceBaggage true (only meaningful with
     * allowedTracingUrls, which we do not configure).
     */
    datadogRum.init({
      applicationId: config.datadog_rum_application_id,
      clientToken: config.datadog_rum_client_token,
      site: config.datadog_rum_site,
      service: config.datadog_rum_service,
      env: config.server_env || 'development',
      version: config.nova_version,
      sessionSampleRate: config.datadog_rum_session_sample_rate ?? 10,
      sessionReplaySampleRate: config.datadog_rum_session_replay_sample_rate ?? 0,
      profilingSampleRate: config.datadog_rum_profiling_sample_rate ?? 0,
      propagateTraceBaggage: config.datadog_rum_propagate_trace_baggage ?? false,
      trackUserInteractions: config.datadog_rum_track_user_interactions ?? true,
      trackResources: config.datadog_rum_track_resources ?? false,
      trackLongTasks: config.datadog_rum_track_long_tasks ?? false,
      defaultPrivacyLevel: config.datadog_rum_default_privacy_level ?? 'mask-user-input',
      beforeSend(event) {
        return scrubModifiableRumFields(event);
      }
    });
  } catch (error) {
    console.error('Error initializing Datadog RUM:', error);
  }
}

/**
 * Check if Datadog RUM is initialized
 * @returns {boolean} - True if RUM is initialized
 */
function isRumInitialized() {
  try {
    // getInternalContext returns undefined if RUM is not initialized
    return datadogRum.getInternalContext() !== undefined;
  } catch {
    return false;
  }
}

/**
 * Set user context for Datadog RUM
 * @param {object|null} user - User object with id, email, etc.
 */
export function setDatadogRumUser(user) {
  // Skip if RUM is not initialized
  if (!isRumInitialized()) {
    return;
  }

  if (!user?.id) {
    datadogRum.clearUser();
    return;
  }

  datadogRum.setUser({
    id: user.id,
    orgId: user.orgId
  });
}
